import { PlayedPosition } from '@statsbomb/parachute-types';
import { t } from 'i18next';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { filtersAtom } from '@/atoms/filters/filter';
import { loadablePlayerSelectedGamesForQueryAtom } from '@/atoms/filters/player/playerGames';
import { PositionOption } from '@/types/filters';
import { convertFilterParamsToString, hasEmptyInFilters } from '@/utils/api';
import { haveSameValues } from '@/utils/array';
import { convertToDropdownOption, optionsToValueStrings } from '@/utils/object';
import { normalisePositionPercentagesTo100, positionPercentagesTo90sPlayed } from '@/utils/player';
import { objSnakeToCamel } from '@/utils/queries';
import { atomWithDebounce } from '../../debounce';
import { playerIdAtom } from '../../player/player';
import { fetchClientAtom } from '../client';
import { playerAggsMinutesPlayedWithFallbackAtom } from './playerMinsPlayed';
import { buildPositionOptions } from './utils';

type PlayerPositionsFilter = { in: { game_id: string[] } } | null;

export const playerPositionsFilterDebounceObject = atomWithDebounce<PlayerPositionsFilter>(null);
const { debouncedValueAtom: playerPositionsFilterAtom } = playerPositionsFilterDebounceObject;

export const rawPlayerPositionsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerPositions', get(playerIdAtom), get(playerPositionsFilterAtom)] as const;

  const queryFn = async ({ queryKey: [_, playerId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!playerId || hasEmptyInFilters(filterParams)) return [];

    return (await fetch(`/player/${playerId}/positions${convertFilterParamsToString(filterParams)}`)) as Promise<
      PlayedPosition[]
    >;
  };
  return { queryKey, queryFn };
});

// True once the player's games have settled AND the positions query is keyed on those settled games.
// Exported so other player-switching-sensitive readers (e.g. the AI summary, which shares this same
// games -> debounced filter chain via useSetPlayerEventDataDebouncedFilterParams) can wait on it too.
export const playerPositionsSettledAtom = atom(get => {
  const gamesLoadable = get(loadablePlayerSelectedGamesForQueryAtom);
  if (gamesLoadable.state !== 'hasData') return false;
  return haveSameValues(optionsToValueStrings(gamesLoadable.data), get(playerPositionsFilterAtom)?.in.game_id ?? []);
});

// The player's played positions, most played first. Every reader of "the player's most played position"
// (the radar's default template and its position group, the AI summary's primary position) takes it from
// here, so they agree by construction rather than by the API's sort order.
export const playerPositionsByPercentageAtom = atom(async get => {
  const rawPlayerPositions = await get(rawPlayerPositionsAtom);
  return rawPlayerPositions.data.map(objSnakeToCamel).sort((a, b) => b.percentage - a.percentage);
});

// Positions under 1% go into "Other" at the end. That never moves the first option: percentages sum to
// 100, so the most played position is always at least 1%.
export const playerPositionsOptionsAtom = atom(async (get): Promise<PositionOption[]> => {
  // During a player switch the selected-games atom briefly serves the previous player's games (and the
  // positions filter is debounced behind it). Surfacing options before the positions query is keyed on
  // the new player's settled games would let the position prune drop a selection that is actually valid.
  if (!get(playerPositionsSettledAtom)) return [];

  const playerPositions = await get(playerPositionsByPercentageAtom);

  const { positionOptions, otherPositions } = buildPositionOptions(playerPositions);

  return [
    ...positionOptions,
    ...(otherPositions.length > 0
      ? [{ ...convertToDropdownOption(t('other', { ns: 'filters' }), otherPositions.join(',')), other: true }]
      : []),
  ];
});

export const unwrappedPlayerPositionsOptionsAtom = unwrap(playerPositionsOptionsAtom, prev => prev || []);

export const playerPositionsAndMinutesAtom = atom(async get => {
  const playerPositionsData = (await get(rawPlayerPositionsAtom)).data;
  const playerTotalMinutes = await get(playerAggsMinutesPlayedWithFallbackAtom);

  const selectedPositions = get(filtersAtom).positions;

  const filteredPlayerPositions = normalisePositionPercentagesTo100(
    playerPositionsData.filter(({ position }) => selectedPositions.some(selected => selected === position)),
  );

  const playerPositions = positionPercentagesTo90sPlayed(
    filteredPlayerPositions.length ? filteredPlayerPositions : playerPositionsData,
    playerTotalMinutes,
  );

  return {
    playerPositions,
    playerTotalMinutes,
  };
});

export const unwrappedPlayerPositionsAndMinutesAtom = unwrap(
  playerPositionsAndMinutesAtom,
  prev => prev || { playerPositions: {}, playerTotalMinutes: 0 },
);
